<script setup>
// NOTICE: KAPTION-INTEGRATION - Sidebar toggle switch with copilot branding and notification badges
import Button from 'dashboard/components-next/button/Button.vue';
import NotificationDropdown from 'dashboard/components/DashboardApp/NotificationDropdown.vue';
import { useUISettings } from 'dashboard/composables/useUISettings';
import { useMapGetter } from 'dashboard/composables/store';
// NOTICE: KAPTION-INTEGRATION - feature-permissions.DYNAMIC.1
import { useFeaturePermissions } from 'dashboard/composables/useFeaturePermissions';
import { computed, ref } from 'vue';
import { useKeyboardEvents } from 'dashboard/composables/useKeyboardEvents';
import { useI18n } from 'vue-i18n';

const { t } = useI18n();
const { updateUISettings } = useUISettings();

const { uiSettings } = useUISettings();
// NOTICE: KAPTION-INTEGRATION - Default to true (open) if never explicitly set by user
const isContactSidebarOpen = computed(
  () => uiSettings.value.is_contact_sidebar_open ?? true
);

// NOTICE: KAPTION-INTEGRATION - Badge notifications
const totalBadgeCount = useMapGetter('dashboardAppBadges/getTotalBadgeCount');
const hasAnyBadges = useMapGetter('dashboardAppBadges/hasAnyBadges');
const allDashboardApps = useMapGetter('dashboardApps/getRecords');

// Computed to check if we should show badge
const showBadge = computed(
  () => hasAnyBadges.value && !isContactSidebarOpen.value
);

// Format badge count for display
const formattedBadgeCount = computed(() => {
  const count = totalBadgeCount.value;
  if (count > 99) return '99+';
  return count.toString();
});

// Get sidebar apps the user may use (instant hiding; BE index filters too)
const { canUseFeature } = useFeaturePermissions();
const sidebarApps = computed(() =>
  allDashboardApps.value.filter(
    app =>
      app.placement === 'sidebar' && canUseFeature(`dashboard_app:${app.id}`)
  )
);

// Hover state for dropdown
const isHovering = ref(false);
const showDropdown = computed(() => isHovering.value && hasAnyBadges.value);

// NOTICE: KAPTION-INTEGRATION - Toggle sidebar open/closed
const toggleSidebar = () => {
  updateUISettings({
    is_contact_sidebar_open: !isContactSidebarOpen.value,
    is_copilot_panel_open: false,
  });
};

// NOTICE: KAPTION-INTEGRATION - Open sidebar and switch to specific app tab
const handleSelectApp = app => {
  // Find the tab index for this app
  const appIndex = sidebarApps.value.findIndex(a => a.id === app.id);
  const tabIndex = appIndex >= 0 ? appIndex + 1 : 0; // +1 because 0 is contact tab

  updateUISettings({
    is_contact_sidebar_open: true,
    is_copilot_panel_open: false,
    sidebar_app_tab: tabIndex,
  });

  // Close the dropdown
  isHovering.value = false;
};

// Handle clear all - close dropdown
const handleClearAll = () => {
  isHovering.value = false;
};

// NOTICE: KAPTION-INTEGRATION - Handle action button clicks from notifications
const handleActionClick = () => {
  // The NotificationDropdown already sends the action to the app via postMessage
  // We just need to close the dropdown after an action is taken
  isHovering.value = false;
};

// NOTICE: KAPTION-INTEGRATION - Copilot sparkles icon when closed, chevron when open
const buttonIcon = computed(() =>
  isContactSidebarOpen.value
    ? 'i-lucide-chevron-right'
    : 'i-woot-copilot-sparkles'
);

const tooltipText = computed(() =>
  isContactSidebarOpen.value
    ? t('CONVERSATION.SIDEBAR.HIDE_TOOLTIP')
    : t('CONVERSATION.SIDEBAR.OPEN_COPILOT_TOOLTIP')
);

// NOTICE: KAPTION-INTEGRATION - Dynamic tooltip offset when badge is showing
// Moves tooltip higher to avoid collision with badge
const tooltipOffset = computed(() => (showBadge.value ? [-12, 4] : [0, 4]));

const keyboardEvents = {
  'Alt+KeyO': {
    action: toggleSidebar,
  },
};
useKeyboardEvents(keyboardEvents);
</script>

<template>
  <!-- NOTICE: KAPTION-INTEGRATION - Copilot toggle button with violet branding and notification badge -->
  <div
    class="flex justify-center items-center absolute top-36 xl:top-24 ltr:right-2 rtl:left-2"
    @mouseenter="isHovering = true"
    @mouseleave="isHovering = false"
  >
    <!-- Notification dropdown (shown on hover when there are badges) -->
    <Transition name="dropdown">
      <div
        v-if="showDropdown"
        class="absolute ltr:right-full rtl:left-full ltr:mr-2 rtl:ml-2 top-0"
      >
        <NotificationDropdown
          @select-app="handleSelectApp"
          @clear-all="handleClearAll"
          @action-click="handleActionClick"
        />
      </div>
    </Transition>

    <!-- Button with badge -->
    <div class="relative">
      <Button
        v-tooltip="{
          content: tooltipText,
          placement: 'left',
          offset: tooltipOffset,
        }"
        class="!rounded-full !h-10 !w-10 !text-xl bg-n-copilot-9 hover:bg-n-copilot-10 text-white transition-all duration-200"
        :icon="buttonIcon"
        @click="toggleSidebar"
      />

      <!-- Badge indicator -->
      <Transition name="badge">
        <span
          v-if="showBadge"
          class="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 flex items-center justify-center text-[10px] font-bold rounded-full bg-n-ruby-9 text-white shadow-sm border border-white dark:border-n-slate-1"
        >
          {{ formattedBadgeCount }}
        </span>
      </Transition>
    </div>
  </div>
</template>

<style scoped>
/* Dropdown animation */
.dropdown-enter-active,
.dropdown-leave-active {
  transition: all 0.2s ease;
}

.dropdown-enter-from,
.dropdown-leave-to {
  opacity: 0;
  transform: translateX(8px);
}

/* Badge animation */
.badge-enter-active,
.badge-leave-active {
  transition: all 0.2s ease;
}

.badge-enter-from,
.badge-leave-to {
  opacity: 0;
  transform: scale(0.5);
}
</style>
